import { useThemeMode } from './ThemeContext';
import type { ThemeMode } from './ptcThemes';

/**
 * Semantic status/complexity colors, calibrated per theme — the single home
 * for the hex values previously inlined across DashboardPage (and intended
 * for reuse by other pages as they grow status visuals).
 *
 * Light keeps the app's established palette (solid saturated chips, white
 * glyphs). Dark is a deliberate recalibration, not an inversion: chips become
 * deep color-tinted wells with bright glyphs (a saturated slab of #d83b01 on
 * a dark card glares), bars drop a little luminance, and text variants are
 * tuned for ≥4.5:1 (WCAG AA) on the dark card/page surfaces.
 */
export type SemanticColorKey =
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'purple'
  | 'neutral';

export interface SemanticColor {
  /** Container fill — stat-tile icon chips and similar filled accents. */
  fill: string;
  /** Foreground rendered on top of `fill` (the chip's icon/text). */
  onFill: string;
  /** Strong solid — data-viz bars/segments; ≥3:1 against page and card. */
  solid: string;
  /** Colored text placed directly on page/card surfaces; WCAG AA. */
  text: string;
}

export const semanticColorMaps: Record<
  ThemeMode,
  Record<SemanticColorKey, SemanticColor>
> = {
  light: {
    brand: { fill: '#0078d4', onFill: '#ffffff', solid: '#0078d4', text: '#0078d4' },
    success: { fill: '#107c10', onFill: '#ffffff', solid: '#107c10', text: '#107c10' },
    warning: { fill: '#ff8c00', onFill: '#ffffff', solid: '#ff8c00', text: '#a35d00' },
    danger: { fill: '#d83b01', onFill: '#ffffff', solid: '#d83b01', text: '#d83b01' },
    purple: { fill: '#8764b8', onFill: '#ffffff', solid: '#8764b8', text: '#6b4fa0' },
    neutral: { fill: '#605e5c', onFill: '#ffffff', solid: '#605e5c', text: '#605e5c' },
  },
  dark: {
    brand: { fill: '#15304b', onFill: '#5eb2ec', solid: '#3a96de', text: '#5eb2ec' },
    success: { fill: '#16321a', onFill: '#7bc87b', solid: '#2ea043', text: '#7bc87b' },
    warning: { fill: '#3a2a10', onFill: '#f5a83c', solid: '#e08a00', text: '#f5a83c' },
    danger: { fill: '#3b1c12', onFill: '#ff8a68', solid: '#e25f38', text: '#ff8a68' },
    purple: { fill: '#2c2542', onFill: '#b9a5e3', solid: '#8e6cc8', text: '#b9a5e3' },
    neutral: { fill: '#262e38', onFill: '#a8b2bd', solid: '#5b6573', text: '#a8b2bd' },
  },
};

/** Client-complexity buckets → semantic keys (thresholds live with callers). */
export const complexitySemanticKey = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
} as const satisfies Record<'low' | 'medium' | 'high', SemanticColorKey>;

/** The semantic palette for the active theme mode. */
export function useSemanticColors(): Record<SemanticColorKey, SemanticColor> {
  const { mode } = useThemeMode();
  return semanticColorMaps[mode];
}
